interface DestinationTips {
  transport: string;
  etiquette: string;
  safety: string;
  food: string;
}

interface RegionalConfig {
  currency: string;
  language: string;
  timezone: string;
  emergencyNumbers: {
    police: string;
    medical: string;
    fire: string;
  };
  plugType?: string;
  voltage?: string;
  culturalNorms: string[];
  commonTransport: string[];
  foodSpecialties: string[];
  safetyConsiderations: string[];
  arrivalTips?: string[]; // Tips for airport arrival and immigration
}

// Regional configuration database for accurate, localized information
const REGIONAL_CONFIGS: Record<string, RegionalConfig> = {
  // Europe
  'italy': {
    currency: 'Euro (€)',
    language: 'Italian',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '113', medical: '118', fire: '115' },
    plugType: 'Type C/F/L (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Dress modestly in churches', 'Meal times: lunch 12:30-2:30pm, dinner after 8pm', 'Tipping: round up bills, 10% for excellent service'],
    commonTransport: ['Metro systems in major cities', 'High-speed trains (Frecciarossa)', 'Bus networks', 'Taxi services'],
    foodSpecialties: ['Regional pasta dishes', 'Fresh seafood', 'Local wines', 'Gelato', 'Coffee culture'],
    safetyConsiderations: ['Pickpockets in tourist areas', 'Traffic in major cities', 'Summer heat precautions']
  },
  'greece': {
    currency: 'Euro (€)',
    language: 'Greek',
    timezone: 'EET (UTC+2)',
    emergencyNumbers: { police: '100', medical: '166', fire: '199' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Orthodox church customs', 'Late dining: lunch 2-4pm, dinner after 9pm', 'Philoxenia (hospitality) culture'],
    commonTransport: ['Metro in Athens', 'Ferry connections', 'Bus networks', 'Taxi services'],
    foodSpecialties: ['Mezze culture', 'Fresh seafood', 'Olive oil', 'Feta cheese', 'Ouzo'],
    safetyConsiderations: ['Summer heat (40°C+)', 'Sea safety', 'Tourist area scams']
  },
  'france': {
    currency: 'Euro (€)',
    language: 'French',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '17', medical: '15', fire: '18' },
    plugType: 'Type C/E (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Always greet with "Bonjour/Bonsoir"', 'Formal dining etiquette', 'Handshakes and personal space'],
    commonTransport: ['Metro systems', 'TGV high-speed trains', 'Regional trains', 'Bus networks'],
    foodSpecialties: ['Regional cuisine', 'Wine culture', 'Cheese varieties', 'Pastries', 'Market culture'],
    safetyConsiderations: ['Metro pickpockets', 'Tourist scams', 'Strike days transport disruption']
  },
  'spain': {
    currency: 'Euro (€)',
    language: 'Spanish',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '091', medical: '061', fire: '080' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Late meal times', 'Siesta culture 2-5pm', 'Social kissing greetings', 'Tapas sharing culture'],
    commonTransport: ['Metro systems', 'AVE high-speed trains', 'Bus networks', 'Bike sharing'],
    foodSpecialties: ['Tapas culture', 'Regional specialties', 'Jamón ibérico', 'Local wines', 'Market culture'],
    safetyConsiderations: ['Pickpockets in tourist areas', 'Late night safety', 'Summer heat']
  },
  'portugal': {
    currency: 'Euro (€)',
    language: 'Portuguese',
    timezone: 'WET (UTC+0)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Friendly, welcoming culture', 'Traditional meal times', 'Conservative dress in religious sites'],
    commonTransport: ['Trams and metros', 'Regional trains', 'Bus networks', 'Taxi services'],
    foodSpecialties: ['Seafood dishes', 'Pastéis de nata', 'Port wine', 'Local markets'],
    safetyConsiderations: ['Generally very safe', 'Ocean currents', 'Tourist area awareness']
  },
  'turkey': {
    currency: 'Turkish Lira (₺)',
    language: 'Turkish',
    timezone: 'TRT (UTC+3)',
    emergencyNumbers: { police: '155', medical: '112', fire: '110' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Islamic customs', 'Hospitality culture', 'Conservative dress in mosques', 'Haggling in bazaars'],
    commonTransport: ['Metro systems', 'Dolmuş (shared taxis)', 'Bus networks', 'Ferry services'],
    foodSpecialties: ['Turkish breakfast', 'Kebabs', 'Turkish tea and coffee', 'Baklava', 'Street food'],
    safetyConsiderations: ['Tourist area scams', 'Political awareness', 'Conservative areas dress codes']
  },
  'united kingdom': {
    currency: 'British Pound (£)',
    language: 'English',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '999', medical: '999', fire: '999' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Queueing culture', 'Pub etiquette', 'Weather conversation', 'Politeness and "sorry"'],
    commonTransport: ['Underground/Metro', 'Bus networks', 'Train services', 'Black cabs'],
    foodSpecialties: ['Traditional pub food', 'Fish and chips', 'Afternoon tea', 'Local ales', 'Regional specialties'],
    safetyConsiderations: ['Generally very safe', 'Weather preparedness', 'Late night city centers']
  },
  'germany': {
    currency: 'Euro (€)',
    language: 'German',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '110', medical: '112', fire: '112' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Punctuality crucial', 'Direct communication', 'Recycling serious', 'Quiet hours 10pm-6am'],
    commonTransport: ['U-Bahn/S-Bahn', 'Regional trains (DB)', 'Bus networks', 'Bike culture'],
    foodSpecialties: ['Regional cuisines', 'Beer culture', 'Bread varieties', 'Sausages', 'Market culture'],
    safetyConsiderations: ['Very safe', 'Bike traffic awareness', 'Christmas market crowds']
  },
  'netherlands': {
    currency: 'Euro (€)',
    language: 'Dutch',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Direct communication', 'Excellent English', 'Bike culture priority', 'Going Dutch payment'],
    commonTransport: ['Extensive bike paths', 'Tram systems', 'Train networks (NS)', 'Bus services'],
    foodSpecialties: ['Cheese culture', 'Stroopwafel', 'Brown café culture', 'Herring', 'Local markets'],
    safetyConsiderations: ['Very safe', 'Bike traffic awareness', 'Canal safety']
  },
  'austria': {
    currency: 'Euro (€)',
    language: 'German',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '133', medical: '144', fire: '122' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Formal culture', 'Punctuality crucial', 'Coffee house culture', 'Mountain safety'],
    commonTransport: ['U-Bahn systems', 'ÖBB trains', 'Bus networks', 'Mountain transport'],
    foodSpecialties: ['Coffee house culture', 'Schnitzel', 'Apple strudel', 'Wine culture', 'Alpine cuisine'],
    safetyConsiderations: ['Very safe', 'Mountain safety', 'Winter conditions']
  },
  'switzerland': {
    currency: 'Swiss Franc (CHF)',
    language: 'German/French/Italian',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '117', medical: '144', fire: '118' },
    plugType: 'Type J (Swiss 3-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Punctuality essential', 'Quiet culture', 'Expensive prices', 'Mountain culture'],
    commonTransport: ['Swiss Rail system', 'Mountain railways', 'Bus networks', 'Cable cars'],
    foodSpecialties: ['Cheese fondue', 'Chocolate', 'Alpine cuisine', 'Wine regions', 'Local markets'],
    safetyConsiderations: ['Extremely safe', 'Mountain safety', 'Weather changes']
  },
  'ireland': {
    currency: 'Euro (€)',
    language: 'English/Irish',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '999', medical: '999', fire: '999' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Friendly culture', 'Pub culture', 'Weather chat', 'Rounds system drinking'],
    commonTransport: ['Bus networks', 'Train services', 'Taxis', 'Walking culture'],
    foodSpecialties: ['Pub grub', 'Irish breakfast', 'Guinness', 'Traditional stew', 'Soda bread'],
    safetyConsiderations: ['Very safe', 'Weather preparedness', 'Rural road conditions']
  },

  // Asia
  'japan': {
    currency: 'Japanese Yen (¥)',
    language: 'Japanese',
    timezone: 'JST (UTC+9)',
    emergencyNumbers: { police: '110', medical: '119', fire: '119' },
    plugType: 'Type A/B (US-style 2/3-pin flat)',
    voltage: '100V, 50/60Hz',
    culturalNorms: ['Bowing culture', 'Shoe removal', 'No tipping', 'Quiet public behavior', 'Business card etiquette'],
    commonTransport: ['JR Rail network', 'Metro systems', 'IC Cards (Suica/Pasmo)', 'Shinkansen bullet trains'],
    foodSpecialties: ['Regional specialties', 'Ramen culture', 'Sushi etiquette', 'Convenience store food', 'Seasonal ingredients'],
    safetyConsiderations: ['Extremely safe', 'Natural disasters preparation', 'Lost & found system', 'Bicycle theft prevention']
  },
  'south korea': {
    currency: 'Korean Won (₩)',
    language: 'Korean',
    timezone: 'KST (UTC+9)',
    emergencyNumbers: { police: '112', medical: '119', fire: '119' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '220V, 60Hz',
    culturalNorms: ['Age hierarchy respect', 'Bow greetings', 'Shoe removal', 'Group dining culture'],
    commonTransport: ['Subway systems', 'KTX high-speed trains', 'Bus networks', 'Taxi apps'],
    foodSpecialties: ['Kimchi culture', 'BBQ dining', 'Street food markets', 'Soju drinking', 'Banchan side dishes'],
    safetyConsiderations: ['Very safe', 'Women-only subway cars', 'Late night safety', 'Language barriers']
  },
  'china': {
    currency: 'Chinese Yuan (¥)',
    language: 'Mandarin Chinese',
    timezone: 'CST (UTC+8)',
    emergencyNumbers: { police: '110', medical: '120', fire: '119' },
    plugType: 'Type A/C/I (mixed - bring universal adapter)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Face culture', 'Business card etiquette', 'Tea culture', 'Group harmony'],
    commonTransport: ['High-speed rail', 'Metro systems', 'Didi ride-sharing', 'Bus networks'],
    foodSpecialties: ['Regional cuisines', 'Tea culture', 'Dim sum', 'Hot pot', 'Street food'],
    safetyConsiderations: ['Generally safe', 'Air quality', 'Internet restrictions', 'Language barriers']
  },
  'thailand': {
    currency: 'Thai Baht (฿)',
    language: 'Thai',
    timezone: 'ICT (UTC+7)',
    emergencyNumbers: { police: '191', medical: '1669', fire: '199' },
    plugType: 'Type A/B/C/O (mixed - bring universal adapter)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Wai greeting', 'King respect', 'Buddhist customs', 'Shoe removal temples'],
    commonTransport: ['BTS/MRT systems', 'Tuk-tuks', 'Songthaews', 'Motorbike taxis'],
    foodSpecialties: ['Street food culture', 'Spicy cuisine', 'Fresh fruits', 'Pad Thai', 'Tom Yum'],
    safetyConsiderations: ['Tourist scams', 'Food safety', 'Traffic awareness', 'Temple dress codes']
  },
  'singapore': {
    currency: 'Singapore Dollar (S$)',
    language: 'English/Mandarin/Malay/Tamil',
    timezone: 'SGT (UTC+8)',
    emergencyNumbers: { police: '999', medical: '995', fire: '995' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Multicultural respect', 'No chewing gum', 'Strict laws', 'Queueing culture'],
    commonTransport: ['MRT system', 'Bus networks', 'Grab rides', 'Walking culture'],
    foodSpecialties: ['Hawker centers', 'Multicultural fusion', 'Hainanese chicken rice', 'Chili crab', 'Singapore sling'],
    safetyConsiderations: ['Extremely safe', 'Strict law enforcement', 'Drug penalties', 'Heat precautions']
  },
  'hong kong': {
    currency: 'Hong Kong Dollar (HK$)',
    language: 'Cantonese/English',
    timezone: 'HKT (UTC+8)',
    emergencyNumbers: { police: '999', medical: '999', fire: '999' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '220V, 50Hz',
    culturalNorms: ['British-Chinese blend', 'Business card etiquette', 'Dim sum culture', 'Queueing respect'],
    commonTransport: ['MTR system', 'Octopus card', 'Trams', 'Star Ferry'],
    foodSpecialties: ['Dim sum', 'Char siu', 'Milk tea', 'Egg tarts', 'Wonton noodles'],
    safetyConsiderations: ['Very safe', 'Dense crowds', 'Political awareness', 'Typhoon season']
  },
  'india': {
    currency: 'Indian Rupee (₹)',
    language: 'Hindi/English + regional',
    timezone: 'IST (UTC+5:30)',
    emergencyNumbers: { police: '100', medical: '102', fire: '101' },
    plugType: 'Type C/D/M (mixed - bring universal adapter)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Namaste greeting', 'Right hand use', 'Religious respect', 'Diverse cultures'],
    commonTransport: ['Train networks', 'Metro systems', 'Auto-rickshaws', 'App-based rides'],
    foodSpecialties: ['Regional cuisines', 'Vegetarian options', 'Spice levels', 'Street food', 'Chai culture'],
    safetyConsiderations: ['Health precautions', 'Traffic safety', 'Scam awareness', 'Cultural sensitivity']
  },
  'indonesia': {
    currency: 'Indonesian Rupiah (Rp)',
    language: 'Indonesian',
    timezone: 'Multiple zones (UTC+7 to +9)',
    emergencyNumbers: { police: '110', medical: '118', fire: '113' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Islamic majority customs', 'Indonesian hospitality', 'Respect for elders', 'Island diversity'],
    commonTransport: ['Grab rides', 'Ojek (motorbikes)', 'Angkot (minivans)', 'Inter-island flights'],
    foodSpecialties: ['Island cuisines', 'Nasi dishes', 'Tropical fruits', 'Spice combinations', 'Local markets'],
    safetyConsiderations: ['Traffic awareness', 'Natural disasters', 'Health precautions', 'Island safety']
  },
  'vietnam': {
    currency: 'Vietnamese Dong (₫)',
    language: 'Vietnamese',
    timezone: 'ICT (UTC+7)',
    emergencyNumbers: { police: '113', medical: '115', fire: '114' },
    plugType: 'Type A/C/G (mixed - bring universal adapter)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Respectful greetings', 'Haggling expected', 'Conservative dress in temples', 'Coffee culture'],
    commonTransport: ['Grab rides', 'Motorbike taxis', 'Buses', 'Trains'],
    foodSpecialties: ['Pho', 'Banh mi', 'Fresh spring rolls', 'Coffee culture', 'Street food'],
    safetyConsiderations: ['Traffic chaos', 'Petty theft', 'Food safety', 'Weather awareness']
  },
  'malaysia': {
    currency: 'Malaysian Ringgit (RM)',
    language: 'Malay/English',
    timezone: 'MYT (UTC+8)',
    emergencyNumbers: { police: '999', medical: '999', fire: '994' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '240V, 50Hz',
    culturalNorms: ['Islamic customs', 'Multicultural respect', 'Right hand use', 'Shoe removal'],
    commonTransport: ['LRT/MRT systems', 'Grab rides', 'Buses', 'Taxis'],
    foodSpecialties: ['Nasi lemak', 'Satay', 'Roti canai', 'Laksa', 'Hawker food'],
    safetyConsiderations: ['Generally safe', 'Petty theft awareness', 'Heat precautions', 'Traffic']
  },
  'philippines': {
    currency: 'Philippine Peso (₱)',
    language: 'Filipino/English',
    timezone: 'PHT (UTC+8)',
    emergencyNumbers: { police: '117', medical: '911', fire: '911' },
    plugType: 'Type A/B/C (mixed - US-style common)',
    voltage: '220V, 60Hz',
    culturalNorms: ['Warm hospitality', 'Family-oriented', 'Respect for elders', 'Catholic traditions'],
    commonTransport: ['Jeepneys', 'Tricycles', 'Grab rides', 'Ferries'],
    foodSpecialties: ['Adobo', 'Lechon', 'Sinigang', 'Halo-halo', 'Street food'],
    safetyConsiderations: ['Typhoon season', 'Traffic congestion', 'Petty theft', 'Beach safety']
  },

  // Americas
  'united states': {
    currency: 'US Dollar ($)',
    language: 'English',
    timezone: 'Multiple zones (UTC-5 to -10)',
    emergencyNumbers: { police: '911', medical: '911', fire: '911' },
    plugType: 'Type A/B (US-style 2/3-pin flat)',
    voltage: '120V, 60Hz',
    culturalNorms: ['Tipping culture 18-20%', 'Personal space', 'Casual friendliness', 'Regional variations'],
    commonTransport: ['Car culture dominant', 'Public transit varies', 'Ride-sharing apps', 'Domestic flights', 'Mobile Passport Control (MPC) app for faster customs - US/Canadian citizens & US Green Card holders'],
    foodSpecialties: ['Regional BBQ', 'Fast food culture', 'Craft beer', 'Food trucks', 'Diverse ethnic foods'],
    safetyConsiderations: ['Varies by location', 'Car safety', 'Weather awareness', 'Health insurance important'],
    arrivalTips: ['Download Mobile Passport Control (MPC) app before arrival - skip long customs lines at 30+ airports', 'MPC eligible: US citizens, Canadian citizens, US lawful permanent residents (Green Card)', 'Fill in customs form in app during flight, receive QR code, use expedited MPC lane', 'Also consider Global Entry ($100/5 years) for even faster processing']
  },
  'canada': {
    currency: 'Canadian Dollar (C$)',
    language: 'English/French',
    timezone: 'Multiple zones (UTC-3.5 to -8)',
    emergencyNumbers: { police: '911', medical: '911', fire: '911' },
    plugType: 'Type A/B (US-style 2/3-pin flat)',
    voltage: '120V, 60Hz',
    culturalNorms: ['Politeness culture', 'Bilingual areas', 'Outdoor culture', 'Multiculturalism'],
    commonTransport: ['Car culture', 'Public transit', 'VIA Rail', 'Bush planes'],
    foodSpecialties: ['Poutine', 'Maple syrup', 'Regional cuisines', 'Craft beer', 'Indigenous foods'],
    safetyConsiderations: ['Very safe', 'Weather extremes', 'Wildlife awareness', 'Winter driving']
  },
  'mexico': {
    currency: 'Mexican Peso (MX$)',
    language: 'Spanish',
    timezone: 'Multiple zones (UTC-6 to -8)',
    emergencyNumbers: { police: '911', medical: '911', fire: '911' },
    plugType: 'Type A/B (US-style 2/3-pin flat)',
    voltage: '127V, 60Hz',
    culturalNorms: ['Family-oriented culture', 'Fiesta traditions', 'Catholic influence', 'Warm hospitality'],
    commonTransport: ['Bus networks', 'Collectivos', 'Metro systems', 'Domestic flights'],
    foodSpecialties: ['Regional Mexican cuisines', 'Street tacos', 'Mezcal/Tequila', 'Fresh markets', 'Mole varieties'],
    safetyConsiderations: ['Varies by region', 'Tourist area safety', 'Water precautions', 'Travel advisories']
  },
  'brazil': {
    currency: 'Brazilian Real (R$)',
    language: 'Portuguese',
    timezone: 'Multiple zones (UTC-2 to -5)',
    emergencyNumbers: { police: '190', medical: '192', fire: '193' },
    plugType: 'Type N (Brazilian 3-pin round)',
    voltage: '127/220V, 60Hz (varies by region)',
    culturalNorms: ['Warm, affectionate culture', 'Beach culture', 'Music and dance', 'Carnival traditions'],
    commonTransport: ['Bus networks', 'Metro systems', 'Uber/ride-sharing', 'Domestic flights'],
    foodSpecialties: ['Regional Brazilian cuisines', 'Churrasco', 'Açaí', 'Cachaça', 'Tropical fruits'],
    safetyConsiderations: ['Urban safety awareness', 'Beach safety', 'Health precautions', 'Favela awareness']
  },
  'argentina': {
    currency: 'Argentine Peso (ARS)',
    language: 'Spanish',
    timezone: 'ART (UTC-3)',
    emergencyNumbers: { police: '911', medical: '911', fire: '911' },
    plugType: 'Type C/I (European & Australian style)',
    voltage: '220V, 50Hz',
    culturalNorms: ['European influence', 'Late dining culture', 'Tango culture', 'Mate drinking'],
    commonTransport: ['Bus networks', 'Subte (subway)', 'Taxis/Uber', 'Long-distance buses'],
    foodSpecialties: ['Beef culture', 'Empanadas', 'Malbec wine', 'Italian influence', 'Dulce de leche'],
    safetyConsiderations: ['Urban awareness', 'Economic fluctuations', 'Protests', 'Altitude sickness (Andes)']
  },

  // Africa
  'south africa': {
    currency: 'South African Rand (ZAR)',
    language: '11 official languages',
    timezone: 'SAST (UTC+2)',
    emergencyNumbers: { police: '10111', medical: '10177', fire: '10177' },
    plugType: 'Type M/N (South African 3-pin round)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Rainbow nation diversity', 'Ubuntu philosophy', 'Braai culture', 'Respect for elders'],
    commonTransport: ['Car rental recommended', 'Uber in cities', 'Minibus taxis', 'Domestic flights'],
    foodSpecialties: ['Braai (BBQ)', 'Biltong', 'Boerewors', 'Local wines', 'Diverse cuisines'],
    safetyConsiderations: ['Safety awareness crucial', 'Crime precautions', 'Health precautions', 'Safari safety']
  },
  'egypt': {
    currency: 'Egyptian Pound (EGP)',
    language: 'Arabic',
    timezone: 'EET (UTC+2)',
    emergencyNumbers: { police: '122', medical: '123', fire: '180' },
    plugType: 'Type C/F (European 2-pin round)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Islamic customs', 'Hospitality culture', 'Conservative dress', 'Haggling expected'],
    commonTransport: ['Taxis/Uber', 'Metro (Cairo)', 'Feluccas (Nile)', 'Domestic flights'],
    foodSpecialties: ['Middle Eastern cuisine', 'Ful medames', 'Koshari', 'Arabic coffee', 'Street food'],
    safetyConsiderations: ['Tourist police', 'Cultural sensitivity', 'Health precautions', 'Desert safety']
  },
  'morocco': {
    currency: 'Moroccan Dirham (MAD)',
    language: 'Arabic/Berber/French',
    timezone: 'WET (UTC+0)',
    emergencyNumbers: { police: '19', medical: '15', fire: '15' },
    plugType: 'Type C/E (European 2-pin round)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Islamic customs', 'Moroccan hospitality', 'Conservative dress', 'Mint tea culture'],
    commonTransport: ['Trains (major cities)', 'Grand taxis', 'Buses', 'Domestic flights'],
    foodSpecialties: ['Tajines', 'Couscous', 'Mint tea', 'Pastries', 'Spice markets'],
    safetyConsiderations: ['Medina navigation', 'Haggling culture', 'Health precautions', 'Desert tours safety']
  },
  'kenya': {
    currency: 'Kenyan Shilling (KES)',
    language: 'Swahili/English',
    timezone: 'EAT (UTC+3)',
    emergencyNumbers: { police: '999', medical: '999', fire: '999' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '240V, 50Hz',
    culturalNorms: ['Jambo greetings', 'Respect for elders', 'Safari etiquette', 'Tribal diversity'],
    commonTransport: ['Matatus', 'Taxis', 'Safari vehicles', 'Domestic flights'],
    foodSpecialties: ['Nyama choma', 'Ugali', 'Sukuma wiki', 'Chai', 'Fresh tropical fruits'],
    safetyConsiderations: ['Safari safety', 'Urban awareness', 'Health precautions', 'Wildlife respect']
  },
  'tanzania': {
    currency: 'Tanzanian Shilling (TZS)',
    language: 'Swahili/English',
    timezone: 'EAT (UTC+3)',
    emergencyNumbers: { police: '112', medical: '114', fire: '115' },
    plugType: 'Type D/G (British 3-pin)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Hakuna matata culture', 'Respect for elders', 'Conservative dress', 'Safari etiquette'],
    commonTransport: ['Dala-dala', 'Taxis', 'Safari vehicles', 'Ferries (Zanzibar)'],
    foodSpecialties: ['Ugali', 'Pilau', 'Mishkaki', 'Zanzibar spices', 'Fresh seafood'],
    safetyConsiderations: ['Safari safety', 'Health precautions', 'Beach safety', 'Wildlife awareness']
  },

  // Oceania
  'australia': {
    currency: 'Australian Dollar (AUD)',
    language: 'English',
    timezone: 'Multiple zones (UTC+8 to +11)',
    emergencyNumbers: { police: '000', medical: '000', fire: '000' },
    plugType: 'Type I (Australian 3-pin angled)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Laid-back culture', 'Outdoor lifestyle', 'Pub culture', 'Indigenous respect'],
    commonTransport: ['Car culture', 'Public transport (cities)', 'Domestic flights', 'Coaches'],
    foodSpecialties: ['BBQ culture', 'Fresh seafood', 'Wine regions', 'Coffee culture', 'Bush tucker'],
    safetyConsiderations: ['Wildlife awareness', 'Sun protection', 'Water safety', 'Remote area preparation']
  },
  'new zealand': {
    currency: 'New Zealand Dollar (NZD)',
    language: 'English/Māori',
    timezone: 'NZST (UTC+12)',
    emergencyNumbers: { police: '111', medical: '111', fire: '111' },
    plugType: 'Type I (Australian 3-pin angled)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Outdoor culture', 'Māori respect', 'Environmental consciousness', 'Casual friendliness'],
    commonTransport: ['Car rental recommended', 'InterCity buses', 'Domestic flights', 'Ferries'],
    foodSpecialties: ['Fresh seafood', 'Lamb', 'Wine regions', 'Māori hangi', 'Coffee culture'],
    safetyConsiderations: ['Very safe', 'Outdoor activity safety', 'Weather changes', 'Remote area preparation']
  },

  // Middle East
  'united arab emirates': {
    currency: 'UAE Dirham (AED)',
    language: 'Arabic/English',
    timezone: 'GST (UTC+4)',
    emergencyNumbers: { police: '999', medical: '998', fire: '997' },
    plugType: 'Type G (British 3-pin rectangular)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Islamic customs', 'Modest dress', 'Ramadan observance', 'No public affection'],
    commonTransport: ['Dubai Metro', 'Taxis', 'Uber/Careem', 'Buses'],
    foodSpecialties: ['Arabic cuisine', 'International dining', 'Shawarma', 'Dates', 'Arabic coffee'],
    safetyConsiderations: ['Very safe', 'Heat precautions', 'Cultural sensitivity', 'Alcohol rules']
  },
  'israel': {
    currency: 'Israeli Shekel (₪)',
    language: 'Hebrew/Arabic',
    timezone: 'IST (UTC+2)',
    emergencyNumbers: { police: '100', medical: '101', fire: '102' },
    plugType: 'Type H/C (Israeli 3-pin)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Shabbat observance', 'Security awareness', 'Direct communication', 'Religious diversity'],
    commonTransport: ['Buses', 'Trains', 'Sherut (shared taxis)', 'Light rail'],
    foodSpecialties: ['Falafel', 'Hummus', 'Shakshuka', 'Middle Eastern cuisine', 'Local wines'],
    safetyConsiderations: ['Security checks', 'Political awareness', 'Religious site protocols', 'Heat precautions']
  },
  'jordan': {
    currency: 'Jordanian Dinar (JOD)',
    language: 'Arabic',
    timezone: 'EET (UTC+2)',
    emergencyNumbers: { police: '911', medical: '911', fire: '911' },
    plugType: 'Type B/C/F/G/J (mixed - bring adapter)',
    voltage: '230V, 50Hz',
    culturalNorms: ['Arab hospitality', 'Conservative dress', 'Petra etiquette', 'Respect for royalty'],
    commonTransport: ['Taxis', 'JETT buses', 'Rental cars', 'Tourist shuttles'],
    foodSpecialties: ['Mansaf', 'Falafel', 'Mezze', 'Arabic coffee', 'Bedouin cuisine'],
    safetyConsiderations: ['Very safe', 'Desert precautions', 'Cultural sensitivity', 'Health precautions']
  },

  // Southern Africa
  'zimbabwe': {
    currency: 'US Dollar/ZAR (multicurrency)',
    language: 'English/Shona/Ndebele',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '995', medical: '994', fire: '993' },
    plugType: 'Type D/G (British 3-pin)',
    voltage: '220V, 50Hz',
    culturalNorms: ['Respectful greetings', 'Conservative dress', 'Ask permission for photos', 'Tip service staff'],
    commonTransport: ['Taxis and buses', 'Domestic flights', 'Car rental with guide', 'Commuter omnibus'],
    foodSpecialties: ['Sadza (maize meal)', 'Game meat', 'Muriwo (greens)', 'Mazoe orange'],
    safetyConsiderations: ['Political awareness', 'Cash shortages', 'Health precautions', 'Wildlife safety']
  },
  'zambia': {
    currency: 'Zambian Kwacha (ZMW)',
    language: 'English/Bemba/Nyanja',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '991', medical: '992', fire: '993' },
    culturalNorms: ['Warm hospitality', 'Respectful greetings', 'Conservative dress', 'Ask permission for photos'],
    commonTransport: ['Minibuses', 'Taxis', 'Domestic flights', 'Car rental'],
    foodSpecialties: ['Nshima (maize porridge)', 'Grilled fish', 'Game meat', 'Fresh vegetables'],
    safetyConsiderations: ['Petty theft in cities', 'Health precautions', 'Wildlife safety', 'Road conditions']
  },
  'botswana': {
    currency: 'Botswana Pula (BWP)',
    language: 'English/Setswana',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '999', medical: '997', fire: '998' },
    culturalNorms: ['Friendly culture', 'Respect for elders', 'Conservative dress', 'Environmental respect'],
    commonTransport: ['Car rental recommended', 'Domestic flights', 'Taxis', 'Combis (minibuses)'],
    foodSpecialties: ['Seswaa (pounded meat)', 'Bogobe (porridge)', 'Game meat', 'Mopane worms'],
    safetyConsiderations: ['Very safe', 'Wildlife awareness', 'Sun protection', 'Remote area preparation']
  },
  'namibia': {
    currency: 'Namibian Dollar (NAD)',
    language: 'English/Afrikaans/German',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '10111', medical: '211111', fire: '211111' },
    culturalNorms: ['Multilingual society', 'German influence', 'Conservative dress', 'Wildlife respect'],
    commonTransport: ['Car rental essential', 'Domestic flights', '4x4 for remote areas', 'Intercape buses'],
    foodSpecialties: ['Biltong', 'Game meat', 'German-influenced cuisine', 'Fresh seafood (coast)'],
    safetyConsiderations: ['Very safe', 'Desert driving', 'Wildlife awareness', 'Remote area preparation']
  },
  'mozambique': {
    currency: 'Mozambican Metical (MZN)',
    language: 'Portuguese',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '119', medical: '117', fire: '198' },
    culturalNorms: ['Portuguese influence', 'Warm hospitality', 'Beach culture', 'Conservative in rural areas'],
    commonTransport: ['Chapas (minibuses)', 'Taxis', 'Domestic flights', 'Ferries'],
    foodSpecialties: ['Peri-peri prawns', 'Matapa', 'Fresh seafood', 'Portuguese pastries'],
    safetyConsiderations: ['Petty theft', 'Health precautions', 'Beach safety', 'Road conditions']
  },
  'madagascar': {
    currency: 'Malagasy Ariary (MGA)',
    language: 'Malagasy/French',
    timezone: 'EAT (UTC+3)',
    emergencyNumbers: { police: '117', medical: '124', fire: '118' },
    culturalNorms: ['Fady (taboos) respect', 'French influence', 'Warm hospitality', 'Traditional customs'],
    commonTransport: ['Taxi-brousse (bush taxis)', 'Domestic flights', 'Pousse-pousse (rickshaws)', 'Rental cars'],
    foodSpecialties: ['Romazava (beef stew)', 'Ravitoto', 'Zebu meat', 'Vanilla', 'Fresh seafood'],
    safetyConsiderations: ['Petty theft', 'Road conditions', 'Health precautions', 'Political stability awareness']
  },
  'lesotho': {
    currency: 'Lesotho Loti (LSL)/ZAR',
    language: 'Sesotho/English',
    timezone: 'SAST (UTC+2)',
    emergencyNumbers: { police: '123', medical: '121', fire: '122' },
    culturalNorms: ['Mountain kingdom culture', 'Blanket tradition', 'Respectful greetings', 'Conservative dress'],
    commonTransport: ['4x4 essential for highlands', 'Minibus taxis', 'Horse riding', 'Domestic flights'],
    foodSpecialties: ['Papa (maize porridge)', 'Moroho (greens)', 'Mutton', 'Traditional beer'],
    safetyConsiderations: ['Mountain safety', 'Cold weather', 'Road conditions', 'Altitude awareness']
  },
  'eswatini': {
    currency: 'Swazi Lilangeni (SZL)/ZAR',
    language: 'siSwati/English',
    timezone: 'SAST (UTC+2)',
    emergencyNumbers: { police: '999', medical: '933', fire: '933' },
    culturalNorms: ['Monarchy respect', 'Traditional customs', 'Conservative dress', 'Friendly culture'],
    commonTransport: ['Minibus taxis', 'Car rental', 'Kombis', 'Limited public transport'],
    foodSpecialties: ['Sishwala (porridge)', 'Game meat', 'Traditional beer', 'Biltong'],
    safetyConsiderations: ['Generally safe', 'Health precautions', 'Wildlife awareness', 'Cultural sensitivity']
  },

  // More Caribbean
  'aruba': {
    currency: 'Aruban Florin (AWG)/USD',
    language: 'Papiamento/Dutch/English',
    timezone: 'AST (UTC-4)',
    emergencyNumbers: { police: '911', medical: '911', fire: '911' },
    culturalNorms: ['Multilingual island', 'Relaxed atmosphere', 'Beach culture', 'Dutch influence'],
    commonTransport: ['Rental cars', 'Buses', 'Taxis', 'Hotel shuttles'],
    foodSpecialties: ['Fresh seafood', 'Keshi yena', 'Dutch-influenced cuisine', 'Pan bati'],
    safetyConsiderations: ['Very safe', 'Sun protection', 'Water safety', 'Hurricane season (Jun-Nov)']
  },
  'saint lucia': {
    currency: 'East Caribbean Dollar (XCD)',
    language: 'English/Creole',
    timezone: 'AST (UTC-4)',
    emergencyNumbers: { police: '999', medical: '911', fire: '911' },
    culturalNorms: ['British-French heritage', 'Friendly culture', 'Island time', 'Carnival tradition'],
    commonTransport: ['Rental cars (left-hand drive)', 'Taxis', 'Minibuses', 'Water taxis'],
    foodSpecialties: ['Green fig and saltfish', 'Callaloo soup', 'Rum punch', 'Fresh seafood'],
    safetyConsiderations: ['Generally safe', 'Mountain road caution', 'Beach safety', 'Hurricane season']
  },
  'grenada': {
    currency: 'East Caribbean Dollar (XCD)',
    language: 'English/Creole',
    timezone: 'AST (UTC-4)',
    emergencyNumbers: { police: '911', medical: '434', fire: '911' },
    culturalNorms: ['Spice island culture', 'Friendly locals', 'Cricket passion', 'British influence'],
    commonTransport: ['Rental cars', 'Buses', 'Water taxis', 'Taxis'],
    foodSpecialties: ['Oil down', 'Nutmeg products', 'Fresh seafood', 'Rum punch', 'Spiced chocolate'],
    safetyConsiderations: ['Very safe', 'Beach safety', 'Sun protection', 'Hurricane season awareness']
  },

  // More Europe
  'belgium': {
    currency: 'Euro (€)',
    language: 'Dutch/French/German',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '101', medical: '100', fire: '100' },
    culturalNorms: ['Multilingual etiquette', 'Beer culture', 'Chocolate tradition', 'EU capital formality'],
    commonTransport: ['Excellent train network', 'Metro (Brussels)', 'Trams', 'Bike culture'],
    foodSpecialties: ['Moules-frites', 'Belgian waffles', 'Chocolate', 'Beer varieties', 'Fries'],
    safetyConsiderations: ['Generally safe', 'Pickpockets in tourist areas', 'Terrorism awareness']
  },
  'czech republic': {
    currency: 'Czech Koruna (CZK)',
    language: 'Czech',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '158', medical: '155', fire: '150' },
    culturalNorms: ['Beer culture', 'Formal greetings', 'Quiet public transport', 'Historical respect'],
    commonTransport: ['Excellent public transport', 'Metro (Prague)', 'Trams', 'Trains'],
    foodSpecialties: ['Goulash', 'Svíčková', 'Czech beer', 'Trdelník', 'Dumplings'],
    safetyConsiderations: ['Very safe', 'Tourist scams in Prague', 'Pickpockets', 'Tick awareness (forests)']
  },
  'poland': {
    currency: 'Polish Złoty (PLN)',
    language: 'Polish',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '997', medical: '999', fire: '998' },
    culturalNorms: ['Formal etiquette', 'Catholic traditions', 'Historical respect', 'Name day celebrations'],
    commonTransport: ['PKP trains', 'Metro (Warsaw)', 'Trams', 'Buses', 'Domestic flights'],
    foodSpecialties: ['Pierogi', 'Żurek soup', 'Kielbasa', 'Bigos', 'Vodka'],
    safetyConsiderations: ['Very safe', 'Cold winters', 'Pickpockets in tourist areas']
  },
  'croatia': {
    currency: 'Euro (€)',
    language: 'Croatian',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '192', medical: '194', fire: '193' },
    culturalNorms: ['Mediterranean culture', 'Coffee culture', 'Beach etiquette', 'Historical pride'],
    commonTransport: ['Ferries', 'Buses', 'Rental cars', 'Domestic flights', 'Catamaran services'],
    foodSpecialties: ['Fresh seafood', 'Ćevapi', 'Peka', 'Truffles (Istria)', 'Croatian wines'],
    safetyConsiderations: ['Very safe', 'Sea urchins', 'Sun protection', 'Landmine areas marked']
  },
  'denmark': {
    currency: 'Danish Krone (DKK)',
    language: 'Danish',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    culturalNorms: ['Hygge culture', 'Bike culture', 'Punctuality', 'Equality values', 'Design appreciation'],
    commonTransport: ['Bikes everywhere', 'Metro', 'Trains (DSB)', 'Buses', 'Ferries'],
    foodSpecialties: ['Smørrebrød', 'Danish pastries', 'New Nordic cuisine', 'Craft beer'],
    safetyConsiderations: ['Extremely safe', 'Bike awareness', 'Expensive alcohol']
  },
  'sweden': {
    currency: 'Swedish Krona (SEK)',
    language: 'Swedish',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    culturalNorms: ['Lagom (moderation)', 'Fika culture', 'Nature respect', 'Gender equality', 'Queue culture'],
    commonTransport: ['Tunnelbana (Stockholm)', 'Trains (SJ)', 'Buses', 'Ferries', 'Bike lanes'],
    foodSpecialties: ['Meatballs', 'Gravlax', 'Fika pastries', 'Cinnamon buns', 'Surströmming'],
    safetyConsiderations: ['Extremely safe', 'Dark winters', 'Tick awareness (summer)', 'Cashless society']
  },
  'norway': {
    currency: 'Norwegian Krone (NOK)',
    language: 'Norwegian',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '112', medical: '113', fire: '110' },
    culturalNorms: ['Nature culture', 'Friluftsliv (outdoor life)', 'Equality values', 'Reserved demeanor'],
    commonTransport: ['Trains (scenic routes)', 'Fjord ferries', 'Buses', 'Trams', 'Hurtigruten coastal ferry'],
    foodSpecialties: ['Salmon', 'Brown cheese', 'Lutefisk', 'Rakfisk', 'Cloudberries'],
    safetyConsiderations: ['Extremely safe', 'Weather changes', 'Expensive', 'Wildlife awareness']
  },
  'finland': {
    currency: 'Euro (€)',
    language: 'Finnish/Swedish',
    timezone: 'EET (UTC+2)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    culturalNorms: ['Sisu (perseverance)', 'Sauna culture', 'Personal space', 'Nature respect', 'Silence is golden'],
    commonTransport: ['Trains (VR)', 'Metro (Helsinki)', 'Trams', 'Buses', 'Ferries to Estonia'],
    foodSpecialties: ['Karjalanpiirakka', 'Salmon soup', 'Reindeer', 'Cloudberries', 'Salmiakki'],
    safetyConsiderations: ['Extremely safe', 'Dark winters', 'Cold weather', 'Tick awareness']
  },
  'iceland': {
    currency: 'Icelandic Króna (ISK)',
    language: 'Icelandic',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    culturalNorms: ['Nature respect', 'Elf belief', 'Hot pot culture', 'First name basis', 'Environmental awareness'],
    commonTransport: ['Car rental essential', 'Buses (limited)', 'Domestic flights', '4x4 for highlands'],
    foodSpecialties: ['Fresh fish', 'Lamb', 'Skyr', 'Hot dogs', 'Fermented shark (hákarl)'],
    safetyConsiderations: ['Very safe', 'Weather changes', 'Volcanic activity', 'Road conditions', 'Expensive']
  },

  // More Southeast Asia
  'cambodia': {
    currency: 'Cambodian Riel (KHR)/USD',
    language: 'Khmer',
    timezone: 'ICT (UTC+7)',
    emergencyNumbers: { police: '117', medical: '119', fire: '118' },
    culturalNorms: ['Temple respect', 'Head touching taboo', 'Conservative dress', 'Angkor reverence'],
    commonTransport: ['Tuk-tuks', 'Motorbike taxis', 'Buses', 'Boats', 'Rental bikes'],
    foodSpecialties: ['Amok', 'Lok lak', 'Fish amok', 'Nom banh chok', 'Fresh fruit'],
    safetyConsiderations: ['Landmine awareness (marked areas)', 'Traffic chaos', 'Scams', 'Health precautions']
  },
  'laos': {
    currency: 'Lao Kip (LAK)',
    language: 'Lao',
    timezone: 'ICT (UTC+7)',
    emergencyNumbers: { police: '191', medical: '195', fire: '190' },
    culturalNorms: ['Buddhist customs', 'Laid-back culture', 'Conservative dress', 'Monk respect'],
    commonTransport: ['Tuk-tuks', 'Songthaews', 'Buses', 'Boats (Mekong)', 'Motorbikes'],
    foodSpecialties: ['Laap', 'Sticky rice', 'Tam mak hoong', 'Mok pa', 'Beer Lao'],
    safetyConsiderations: ['UXO awareness (marked)', 'Traffic', 'Health precautions', 'River safety']
  },
  'myanmar': {
    currency: 'Myanmar Kyat (MMK)',
    language: 'Burmese',
    timezone: 'MMT (UTC+6:30)',
    emergencyNumbers: { police: '199', medical: '192', fire: '191' },
    culturalNorms: ['Pagoda respect', 'Longyi tradition', 'Conservative dress', 'Political sensitivity'],
    commonTransport: ['Buses', 'Taxis', 'Circular train (Yangon)', 'Boats', 'Domestic flights'],
    foodSpecialties: ['Mohinga', 'Shan noodles', 'Tea leaf salad', 'Curry dishes'],
    safetyConsiderations: ['Political situation awareness', 'Limited infrastructure', 'Health precautions', 'Cash only']
  },

  // More Pacific
  'fiji': {
    currency: 'Fijian Dollar (FJD)',
    language: 'English/Fijian/Hindi',
    timezone: 'FJT (UTC+12)',
    emergencyNumbers: { police: '917', medical: '911', fire: '910' },
    culturalNorms: ['Bula spirit', 'Kava ceremony', 'Village etiquette', 'Sunday observance', 'Conservative dress'],
    commonTransport: ['Buses', 'Ferries', 'Domestic flights', 'Taxis', 'Boat transfers'],
    foodSpecialties: ['Kokoda', 'Lovo feast', 'Fresh tropical fruit', 'Seafood', 'Indo-Fijian curries'],
    safetyConsiderations: ['Generally safe', 'Cyclone season (Nov-Apr)', 'Beach safety', 'Village protocol']
  },
  'samoa': {
    currency: 'Samoan Tala (WST)',
    language: 'Samoan/English',
    timezone: 'SST (UTC+13)',
    emergencyNumbers: { police: '995', medical: '996', fire: '994' },
    culturalNorms: ['Fa\'a Samoa culture', 'Sunday observance strict', 'Village respect', 'Conservative dress'],
    commonTransport: ['Buses', 'Taxis', 'Rental cars', 'Ferries between islands'],
    foodSpecialties: ['Umu feast', 'Palusami', 'Fresh fish', 'Tropical fruits', 'Oka (raw fish)'],
    safetyConsiderations: ['Very safe', 'Sunday restrictions', 'Cyclone season', 'Village customs respect']
  },
  'tonga': {
    currency: 'Tongan Pa\'anga (TOP)',
    language: 'Tongan/English',
    timezone: 'TOT (UTC+13)',
    emergencyNumbers: { police: '922', medical: '933', fire: '999' },
    culturalNorms: ['Monarchy respect', 'Sunday sabbath strict', 'Conservative dress', 'Traditional customs'],
    commonTransport: ['Taxis', 'Buses (limited)', 'Ferries', 'Domestic flights'],
    foodSpecialties: ['Lu sipi', 'Ota \'ika', 'Root vegetables', 'Fresh seafood', 'Tropical fruits'],
    safetyConsiderations: ['Very safe', 'Sunday closures', 'Cyclone season', 'Limited infrastructure']
  },

  // More Africa
  'ethiopia': {
    currency: 'Ethiopian Birr (ETB)',
    language: 'Amharic',
    timezone: 'EAT (UTC+3)',
    emergencyNumbers: { police: '991', medical: '907', fire: '939' },
    culturalNorms: ['Coffee ceremony', 'Unique calendar', 'Orthodox customs', 'Respectful greetings'],
    commonTransport: ['Minibuses', 'Bajaj (tuk-tuks)', 'Domestic flights', 'Light rail (Addis)'],
    foodSpecialties: ['Injera and wot', 'Tibs', 'Coffee', 'Tella (beer)', 'Berbere spice'],
    safetyConsiderations: ['Political awareness', 'Altitude (Addis 2,400m)', 'Health precautions', 'Petty theft']
  },
  'ghana': {
    currency: 'Ghanaian Cedi (GHS)',
    language: 'English/Twi/Ga',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '191', medical: '193', fire: '192' },
    culturalNorms: ['Warm hospitality', 'Right-hand greetings', 'Colorful kente cloth', 'Festival culture'],
    commonTransport: ['Tro-tros', 'Taxis', 'Buses', 'Domestic flights'],
    foodSpecialties: ['Jollof rice', 'Banku and tilapia', 'Fufu', 'Kelewele', 'Red red'],
    safetyConsiderations: ['Generally safe', 'Health precautions', 'Beach safety', 'Petty theft awareness']
  },
  'rwanda': {
    currency: 'Rwandan Franc (RWF)',
    language: 'Kinyarwanda/French/English',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '112', medical: '912', fire: '112' },
    culturalNorms: ['Umuganda (community service)', 'Plastic bag ban', 'Clean culture', 'Genocide remembrance'],
    commonTransport: ['Motos (motorcycle taxis)', 'Buses', 'Taxis', 'Bike rentals'],
    foodSpecialties: ['Ugali', 'Isombe', 'Brochettes', 'Sambaza (fish)', 'Fresh juices'],
    safetyConsiderations: ['Very safe', 'Gorilla trekking protocols', 'Health precautions', 'Respectful behavior']
  },
  'mauritius': {
    currency: 'Mauritian Rupee (MUR)',
    language: 'English/French/Creole',
    timezone: 'MUT (UTC+4)',
    emergencyNumbers: { police: '999', medical: '114', fire: '995' },
    culturalNorms: ['Multicultural society', 'Beach culture', 'French influence', 'Hindu/Muslim customs'],
    commonTransport: ['Buses', 'Taxis', 'Car rental', 'Catamaran tours'],
    foodSpecialties: ['Dholl puri', 'Vindaye', 'Gateaux piments', 'Rum', 'Creole cuisine'],
    safetyConsiderations: ['Very safe', 'Cyclone season (Nov-May)', 'Sun protection', 'Ocean currents']
  },
  'tunisia': {
    currency: 'Tunisian Dinar (TND)',
    language: 'Arabic/French',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '197', medical: '190', fire: '198' },
    culturalNorms: ['Islamic customs (moderate)', 'French influence', 'Haggling in souks', 'Ramadan observance'],
    commonTransport: ['Louages (shared taxis)', 'Trains (SNCFT)', 'Metro (Tunis)', 'Buses'],
    foodSpecialties: ['Couscous', 'Brik', 'Harissa', 'Mechouia salad', 'Mint tea'],
    safetyConsiderations: ['Generally safe', 'Political awareness', 'Tourist area caution', 'Conservative dress inland']
  },
  'cyprus': {
    currency: 'Euro (€)',
    language: 'Greek/Turkish',
    timezone: 'EET (UTC+2)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    culturalNorms: ['Mediterranean hospitality', 'Beach culture', 'Coffee culture', 'Religious site respect'],
    commonTransport: ['Car rental recommended', 'Buses', 'Taxis', 'Inter-city coaches'],
    foodSpecialties: ['Meze plates', 'Halloumi cheese', 'Souvlaki', 'Fresh seafood', 'Cypriot wine'],
    safetyConsiderations: ['Very safe', 'North/South division awareness', 'Sun protection', 'Beach safety']
  },
  'malta': {
    currency: 'Euro (€)',
    language: 'Maltese/English',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '112', medical: '112', fire: '112' },
    culturalNorms: ['Catholic traditions', 'British influence', 'Festa culture', 'English widely spoken'],
    commonTransport: ['Buses (main transport)', 'Ferries to Gozo', 'Taxis', 'Walking in cities'],
    foodSpecialties: ['Pastizzi', 'Rabbit stew', 'Ftira bread', 'Fresh fish', 'Cisk beer'],
    safetyConsiderations: ['Very safe', 'Summer heat', 'Petty theft in tourist areas', 'Swimming conditions']
  },
  'andorra': {
    currency: 'Euro (€)',
    language: 'Catalan',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '110', medical: '116', fire: '118' },
    culturalNorms: ['Mountain culture', 'Catalan traditions', 'Ski resort etiquette', 'Duty-free shopping'],
    commonTransport: ['Buses', 'Taxis', 'Cable cars', 'Walking'],
    foodSpecialties: ['Trinxat', 'Escudella', 'Mountain cheese', 'Coca bread', 'French-Spanish fusion'],
    safetyConsiderations: ['Very safe', 'Mountain weather', 'Winter driving', 'Altitude awareness']
  },
  'luxembourg': {
    currency: 'Euro (€)',
    language: 'Luxembourgish/French/German',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '113', medical: '112', fire: '112' },
    culturalNorms: ['Multilingual society', 'Banking culture', 'Punctuality', 'Reserved demeanor'],
    commonTransport: ['Free public transport', 'Trains', 'Buses', 'Trams'],
    foodSpecialties: ['Judd mat Gaardebounen', 'Gromperekichelcher', 'Crémant wine', 'French-German fusion'],
    safetyConsiderations: ['Extremely safe', 'Expensive prices', 'Weather preparedness']
  },
  'gibraltar': {
    currency: 'Gibraltar Pound (GIP)/GBP',
    language: 'English',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '199', medical: '190', fire: '190' },
    culturalNorms: ['British culture', 'Mediterranean influence', 'Multicultural', 'Pub culture'],
    commonTransport: ['Walking', 'Cable car', 'Buses', 'Taxis'],
    foodSpecialties: ['Fish and chips', 'Calentita', 'British-Med fusion', 'Fresh seafood'],
    safetyConsiderations: ['Very safe', 'Monkey awareness (Rock)', 'Border crossing times', 'Limited space']
  },
  'kosovo': {
    currency: 'Euro (€)',
    language: 'Albanian/Serbian',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '192', medical: '194', fire: '193' },
    culturalNorms: ['Warm hospitality', 'Coffee culture', 'Young population', 'Religious diversity'],
    commonTransport: ['Buses', 'Taxis', 'Rental cars', 'Walking'],
    foodSpecialties: ['Burek', 'Flija', 'Tavë Kosi', 'Turkish coffee', 'Rakija'],
    safetyConsiderations: ['Generally safe', 'Political awareness', 'Limited infrastructure', 'Cash preferred']
  },
  'north macedonia': {
    currency: 'Macedonian Denar (MKD)',
    language: 'Macedonian',
    timezone: 'CET (UTC+1)',
    emergencyNumbers: { police: '192', medical: '194', fire: '193' },
    culturalNorms: ['Balkan hospitality', 'Orthodox traditions', 'Coffee culture', 'Rakija toasts'],
    commonTransport: ['Buses', 'Taxis', 'Rental cars', 'Walking'],
    foodSpecialties: ['Tavče gravče', 'Ajvar', 'Shopska salad', 'Rakija', 'Turkish coffee'],
    safetyConsiderations: ['Very safe', 'Petty theft awareness', 'Mountain safety', 'Cash preferred']
  },
  'ukraine': {
    currency: 'Ukrainian Hryvnia (UAH)',
    language: 'Ukrainian',
    timezone: 'EET (UTC+2)',
    emergencyNumbers: { police: '102', medical: '103', fire: '101' },
    culturalNorms: ['Warm hospitality', 'Bread and salt greeting', 'Orthodox customs', 'Respect for elders'],
    commonTransport: ['Metro (Kyiv)', 'Trains', 'Marshrutkas', 'Buses'],
    foodSpecialties: ['Borscht', 'Varenyky', 'Salo', 'Chicken Kyiv', 'Horilka'],
    safetyConsiderations: ['Check current situation', 'Political awareness', 'Cold winters', 'Cash preferred']
  },
  'russia': {
    currency: 'Russian Ruble (RUB)',
    language: 'Russian',
    timezone: 'Multiple zones (UTC+2 to +12)',
    emergencyNumbers: { police: '102', medical: '103', fire: '101' },
    culturalNorms: ['Formal culture', 'Hospitality', 'No smiling to strangers', 'Gift-giving traditions'],
    commonTransport: ['Metro systems', 'Trans-Siberian railway', 'Trains', 'Buses', 'Domestic flights'],
    foodSpecialties: ['Borscht', 'Pelmeni', 'Blini', 'Caviar', 'Vodka'],
    safetyConsiderations: ['Check visa requirements', 'Registration mandatory', 'Political awareness', 'Extreme weather']
  },
  'bhutan': {
    currency: 'Bhutanese Ngultrum (BTN)',
    language: 'Dzongkha',
    timezone: 'BTT (UTC+6)',
    emergencyNumbers: { police: '113', medical: '112', fire: '110' },
    culturalNorms: ['Buddhist customs', 'Gross National Happiness', 'Traditional dress required (dzong visits)', 'Environmental respect'],
    commonTransport: ['Organized tours (mandatory)', 'Buses', 'Taxis', 'Walking'],
    foodSpecialties: ['Ema datshi', 'Momos', 'Red rice', 'Butter tea', 'Ara (local alcohol)'],
    safetyConsiderations: ['Very safe', 'Altitude sickness', 'Daily tourist fee', 'Restricted areas']
  },
  'afghanistan': {
    currency: 'Afghan Afghani (AFN)',
    language: 'Dari/Pashto',
    timezone: 'AFT (UTC+4:30)',
    emergencyNumbers: { police: '119', medical: '102', fire: '119' },
    culturalNorms: ['Islamic customs strict', 'Conservative dress', 'Hospitality culture', 'Gender segregation'],
    commonTransport: ['Check current situation', 'Taxis', 'Private vehicles', 'Buses'],
    foodSpecialties: ['Kabuli pulao', 'Mantu', 'Kebabs', 'Naan', 'Green tea'],
    safetyConsiderations: ['Check travel advisories', 'Security situation', 'Cultural sensitivity crucial', 'Female travel restrictions']
  },
  'yemen': {
    currency: 'Yemeni Rial (YER)',
    language: 'Arabic',
    timezone: 'AST (UTC+3)',
    emergencyNumbers: { police: '194', medical: '191', fire: '191' },
    culturalNorms: ['Islamic customs', 'Qat chewing culture', 'Conservative dress', 'Tribal traditions'],
    commonTransport: ['Check current situation', 'Taxis', 'Buses', 'Private vehicles'],
    foodSpecialties: ['Saltah', 'Mandi', 'Fahsa', 'Bint al-sahn', 'Qishr coffee'],
    safetyConsiderations: ['Check travel advisories', 'Security situation critical', 'Health precautions', 'Limited infrastructure']
  },
  'somalia': {
    currency: 'Somali Shilling (SOS)',
    language: 'Somali/Arabic',
    timezone: 'EAT (UTC+3)',
    emergencyNumbers: { police: '888', medical: '888', fire: '888' },
    culturalNorms: ['Islamic customs', 'Nomadic heritage', 'Clan culture', 'Conservative dress'],
    commonTransport: ['Check current situation', 'Taxis', 'Private vehicles', 'Limited public transport'],
    foodSpecialties: ['Bariis iskukaris', 'Sambusa', 'Muqmad', 'Shaah (tea)', 'Halwa'],
    safetyConsiderations: ['Check travel advisories', 'Security situation', 'Limited infrastructure', 'Health precautions']
  },
  'eritrea': {
    currency: 'Eritrean Nakfa (ERN)',
    language: 'Tigrinya/Arabic/English',
    timezone: 'EAT (UTC+3)',
    emergencyNumbers: { police: '113', medical: '114', fire: '116' },
    culturalNorms: ['Coffee ceremony', 'Respect for elders', 'Conservative dress', 'Italian influence'],
    commonTransport: ['Buses', 'Taxis', 'Bicycles', 'Walking'],
    foodSpecialties: ['Zigni', 'Injera', 'Shiro', 'Ful', 'Macchiato'],
    safetyConsiderations: ['Travel permits required', 'Photography restrictions', 'Political sensitivity', 'Currency regulations']
  },
  'south sudan': {
    currency: 'South Sudanese Pound (SSP)',
    language: 'English/Arabic',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '999', medical: '999', fire: '999' },
    culturalNorms: ['Tribal diversity', 'Warm hospitality', 'Conservative dress', 'Respect for elders'],
    commonTransport: ['Check current situation', 'NGO flights', 'Private vehicles', 'River transport'],
    foodSpecialties: ['Kisra', 'Ful medames', 'Bamia', 'Mouloukhiya', 'Tea'],
    safetyConsiderations: ['Check travel advisories', 'Security situation', 'Limited infrastructure', 'Health precautions severe']
  },
  'burundi': {
    currency: 'Burundian Franc (BIF)',
    language: 'Kirundi/French',
    timezone: 'CAT (UTC+2)',
    emergencyNumbers: { police: '117', medical: '112', fire: '118' },
    culturalNorms: ['Drumming traditions', 'Warm hospitality', 'Respect for elders', 'Conservative dress'],
    commonTransport: ['Taxis', 'Minibuses', 'Motorcycle taxis', 'Boats (Lake Tanganyika)'],
    foodSpecialties: ['Ugali', 'Beans', 'Plantains', 'Sambaza fish', 'Primus beer'],
    safetyConsiderations: ['Political awareness', 'Health precautions', 'Limited infrastructure', 'Cash only']
  },
  'the gambia': {
    currency: 'Gambian Dalasi (GMD)',
    language: 'English',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '117', medical: '116', fire: '118' },
    culturalNorms: ['Smiling coast hospitality', 'Islamic customs', 'Music culture', 'Respect for elders'],
    commonTransport: ['Bush taxis', 'Gele-geles', 'Ferries', 'Taxis'],
    foodSpecialties: ['Domoda', 'Benachin', 'Yassa', 'Plasas', 'Attaya tea'],
    safetyConsiderations: ['Generally safe', 'Tourist area awareness', 'Health precautions', 'River safety']
  },
  'togo': {
    currency: 'West African CFA Franc (XOF)',
    language: 'French',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '117', medical: '118', fire: '118' },
    culturalNorms: ['Voodoo traditions', 'French colonial influence', 'Market culture', 'Respect for elders'],
    commonTransport: ['Zemidjan (motorcycle taxis)', 'Taxis', 'Minibuses', 'Walking'],
    foodSpecialties: ['Fufu', 'Akoume', 'Grilled fish', 'Pate', 'Local beer'],
    safetyConsiderations: ['Generally safe', 'Political awareness', 'Health precautions', 'Petty theft']
  },
  'benin': {
    currency: 'West African CFA Franc (XOF)',
    language: 'French',
    timezone: 'WAT (UTC+1)',
    emergencyNumbers: { police: '117', medical: '118', fire: '118' },
    culturalNorms: ['Voodoo birthplace', 'French influence', 'Warm hospitality', 'Market culture'],
    commonTransport: ['Zemidjans (motorcycle taxis)', 'Taxis', 'Bush taxis', 'Minibuses'],
    foodSpecialties: ['Wagashi cheese', 'Acaraje', 'Pate', 'Aloko', 'Tchoukoutou beer'],
    safetyConsiderations: ['Generally safe', 'Health precautions', 'Beach safety', 'Petty theft awareness']
  },
  'mauritania': {
    currency: 'Mauritanian Ouguiya (MRU)',
    language: 'Arabic/French',
    timezone: 'GMT (UTC+0)',
    emergencyNumbers: { police: '117', medical: '101', fire: '118' },
    culturalNorms: ['Islamic customs strict', 'Moorish culture', 'Tea ceremony', 'Conservative dress'],
    commonTransport: ['4x4 essential', 'Bush taxis', 'Domestic flights', 'Camels (desert)'],
    foodSpecialties: ['Thieboudienne', 'Méchoui', 'Couscous', 'Camel meat', 'Mint tea'],
    safetyConsiderations: ['Security awareness', 'Desert travel preparation', 'Health precautions', 'Limited infrastructure']
  },
  'western sahara': {
    currency: 'Moroccan Dirham (MAD)',
    language: 'Arabic/Spanish',
    timezone: 'WET (UTC+0)',
    emergencyNumbers: { police: '19', medical: '15', fire: '15' },
    culturalNorms: ['Sahrawi culture', 'Islamic customs', 'Nomadic heritage', 'Conservative dress'],
    commonTransport: ['4x4 vehicles', 'Limited public transport', 'Private vehicles', 'Walking'],
    foodSpecialties: ['Couscous', 'Grilled meat', 'Dates', 'Mint tea', 'Camel meat'],
    safetyConsiderations: ['Political sensitivity', 'Landmines off-road', 'Limited infrastructure', 'Desert conditions']
  },
  'angola': {
    currency: 'Angolan Kwanza (AOA)',
    language: 'Portuguese',
    timezone: 'WAT (UTC+1)',
    emergencyNumbers: { police: '113', medical: '112', fire: '115' },
    culturalNorms: ['Portuguese influence', 'Music culture (semba, kizomba)', 'Respect for elders', 'Warm hospitality'],
    commonTransport: ['Candongueiros', 'Taxis', 'Domestic flights', 'Boats'],
    foodSpecialties: ['Muamba de galinha', 'Calulu', 'Funge', 'Grilled fish', 'Cuca beer'],
    safetyConsiderations: ['Landmines in rural areas', 'Health precautions', 'Expensive', 'Political awareness']
  },
  'north korea': {
    currency: 'North Korean Won (KPW)',
    language: 'Korean',
    timezone: 'KST (UTC+9)',
    emergencyNumbers: { police: 'Hotel staff', medical: 'Tour guide', fire: 'Hotel staff' },
    culturalNorms: ['Extreme respect for leaders', 'Organized tours only', 'No photography restrictions', 'Dress conservatively'],
    commonTransport: ['Organized tours mandatory', 'Metro (limited)', 'Buses', 'Trains'],
    foodSpecialties: ['Naengmyeon', 'Kimchi', 'BBQ meat', 'Taedonggang beer', 'Soju'],
    safetyConsiderations: ['Tour guide always present', 'Strict rules compliance', 'No independent travel', 'Political sensitivity extreme']
  }
};

// Comprehensive destination-specific data with real prices, timing, and detailed cultural info
const DESTINATION_SPECIFIC_TIPS: Record<string, DestinationTips> = {
  'paris': {
    transport: `📱 **Apps & Passes**
• Citymapper, Moovit for navigation
• Navigo Day Pass: €8.45, Week Pass: €30 (zones 1-5)
• G7 Taxi, Uber, Bolt for rides
• Vélib' bike sharing: €1 for 30min, €5/day pass

🚇 **Metro & Transport**
• Metro runs 5:30am-1:15am (Fri/Sat until 2:15am)
• Buy tickets at stations or with app
• Avoid rush hours: 8-9:30am, 5:30-7:30pm
• RER trains connect suburbs and airports

💰 **Cost Guide**
• Single ticket (t+): €2.10
• Book of 10 tickets: €17.35
• Airport to city: RoissyBus €16.60, RER B €11.80`,
    
    etiquette: `🙏 **Essential French Manners**
• ALWAYS say "Bonjour/Bonsoir" when entering shops/restaurants
• "Excusez-moi" before asking questions
• Use "Madame/Monsieur" (not "garçon")
• Learn basic French - huge appreciation boost

👔 **Dress & Behavior**
• Dress elegantly - Parisians notice style
• Don't eat while walking on streets
• Keep voices moderate in public transport
• Wait to be greeted "Bon appétit" before eating

🍽️ **Dining Culture**
• Never rush meals - dining is social experience
• Service charge (15%) included in bill
• Round up €1-2 for good service
• Don't ask for doggy bags (not common)

⏰ **Social Timing**
• Shops closed Sundays and 12-2pm many places
• Dinner reservations typically after 7:30pm
• August = vacation month, many places closed`,
    
    safety: `🚨 **Emergency Services**
• Police: 17
• Medical (SAMU): 15
• Fire: 18
• EU Emergency: 112
• Tourist Police: 01 53 71 53 71

🛡️ **Safety Tips**
• Generally safe but watch for pickpockets on Metro lines 1, 4, 9
• Avoid empty Metro cars late night
• Keep bags in front of you at tourist sites
• Common scams: petition signing, fake ring finding

📍 **Areas to Be Cautious**
• Gare du Nord/Est at night
• Around Sacré-Cœur (aggressive vendors)
• RER B late at night
• Châtelet-Les Halles area evenings`,
    
    food: `🥐 **Meal Structure & Timing**
• Breakfast (7-9am): Croissant, pain au chocolat, café au lait €5-8
• Lunch (12-2pm): Formule (set menu) €12-20
• Aperitif (6-8pm): Drinks with small bites
• Dinner (7:30-10pm): Multi-course €25-50+

🍷 **Dining Essentials**
• Bread served free, unlimited
• Wine at lunch common and acceptable
• Tap water free: ask for "une carafe d'eau"
• Coffee after dessert, never during meal

🥖 **Must-Try Dishes**
• Fresh boulangerie croissants and pain au chocolat
• Bistro classics: steak-frites, coq au vin, boeuf bourguignon
• Authentic French onion soup (soupe à l'oignon)
• Escargots (snails in garlic butter)
• Duck confit with crispy potatoes
• Traditional cheese course with wine
• Crème brûlée and tarte tatin
• Macarons from specialty patisseries

💳 **Payment & Tipping**
• Service included (15%) in all bills
• Round up €1-2 for good service
• Many places don't accept cards under €10
• High-end: 5-10% extra for exceptional service`
  },
  
  'tokyo': {
    transport: `📱 **Essential Apps & Cards**
• Hyperdia (train schedules) - offline works
• Tokyo Metro/JR East apps - real-time updates
• Google Translate camera - scan signs instantly
• IC Cards: Suica or Pasmo (work everywhere)

🚊 **Train System Hierarchy**
• JR Yamanote Line (city loop): every 2-4 mins
• Tokyo Metro (9 lines): connects major districts
• Private railways: Keio, Odakyu, Tokyu
• Buses: backup option, more complex

💰 **Passes & Pricing**
• JR Pass (7-day): ¥29,650 - worth it for 3+ long trips
• Tokyo Metro 24hr: ¥800 (saves on 5+ rides at ¥170-320 each)
• Greater Tokyo Pass: ¥1,590/day - includes JR + private lines
• Regular fare: ¥170-320 depending on distance

⏰ **Critical Timing**
• Rush hour 7:30-9:30am, 5:30-7:30pm at 200% capacity
• Last trains: 11:30pm-12:30am (varies by line)
• First trains: 5:00am - perfect for early temple visits
• Avoid Friday/Saturday nights - trains packed`,
    
    etiquette: `🙏 **Bowing Protocol**
• Casual greeting: 15° bow
• Business setting: 30° bow
• Formal apology: 45° bow
• Business cards: both hands, bow while receiving, never fold/write on

🏠 **Indoor Customs**
• Remove shoes at genkan (entrance area)
• Slippers provided for indoor use
• NEVER wear slippers on tatami mats
• Separate toilet slippers exist

🥢 **Chopstick Etiquette**
• NEVER stick vertical in rice (funeral rite)
• Don't pass food chopstick-to-chopstick
• Rest on chopstick holder, not bowl rim
• Don't wave or point with chopsticks

💰 **Payment & Service**
• Tipping ACTIVELY REFUSED - implies inadequacy
• Cash still preferred many places
• Place money in tray, not directly in hand
• Service perfection expected (omotenashi)

🗣️ **Communication**
• "Hai" = "I understand" NOT "yes"
• Silence normal - don't fill gaps immediately
• Indirect communication - read between lines
• "Sumimasen" (sorry) used even when not at fault`,
    
    safety: `🚨 **Emergency Numbers**
• Police: 110
• Fire/Medical: 119
• Coast Guard: 118
• Tourist Hotline: 050-3816-2787 (24/7 English)

📊 **Safety Stats**
• Violent crime: 0.3 per 1000 (vs US 3.7 per 1000)
• Pickpocketing almost non-existent
• 90%+ lost items returned through lost & found
• Safe to leave wallet on table

🏪 **Koban (Police Boxes)**
• Every few blocks, staffed 24/7
• English assistance available
• Safe haven if lost
• Help with directions, lost items

🌪️ **Natural Disaster Prep**
• Earthquake apps: Yurekuru, Safety tips (gov app)
• Typhoon season: June-October
• Emergency water/food in major buildings
• Follow evacuation signs if tsunami warning`,
    
    food: `🍱 **Meal Times & Structure**
• Breakfast (7-9am): Traditional rice/miso/fish or Western bread/coffee
• Lunch (11:30am-2pm): Set meals (teishoku) ¥500-1200, quick service
• Dinner (6-9pm): Izakaya (pub) socializing or home meals
• Late night: Convenience stores (quality excellent), ramen until 2am

💴 **Price Ranges**
• Standing bars (tachinomi): ¥200-500 per item
• Chain restaurants (Yoshinoya, Sukiya): ¥400-600 full meals
• Mid-range restaurants: ¥2000-4000 per person
• High-end omakase: ¥15,000+ (book months ahead)

🍜 **Must-Try Dishes**
• Authentic ramen from specialty shops
• Fresh sushi and sashimi at local spots
• Tempura (lightly battered seafood and vegetables)
• Tonkatsu (breaded pork cutlet)
• Yakitori (grilled chicken skewers)
• Okonomiyaki (savory pancake)
• Kaiseki (traditional multi-course meal)
• Wagyu beef yakiniku

🍜 **Dining Etiquette**
• Ramen: SLURP LOUDLY (shows appreciation), finish quickly
• Sushi: Eat immediately when served, ginger only for palate cleanse
• Tempura: Eat hot immediately, light dipping
• Izakaya: Never pour your own drink, pour for others first

🥢 **Food Rules**
• Place chopsticks horizontal when resting
• Use serving chopsticks for shared dishes
• Convenience stores: microwave available, but eating while walking discouraged
• Alcohol: Always pour for others, they'll pour for you

🗓️ **Seasonal Specialties**
• Spring (Mar-May): Sakura-mochi, bamboo shoots, sea bream
• Summer (Jun-Aug): Cold noodles, shaved ice, eel (stamina food)
• Fall (Sep-Nov): Persimmons, pacific saury, matsutake mushrooms
• Winter (Dec-Feb): Hot pot, osechi (New Year), food stalls at illuminations`
  },

  'london': {
    transport: `📱 **Apps & Payment**
• TfL Go app - journey planner, live updates
• Oyster Card or contactless payment (same price)
• Uber, Bolt, Free Now for ride-sharing
• Santander Cycles (Boris Bikes): £2 for 30min

🚇 **Underground & Transport**
• Tube runs 5am-midnight (Night Tube Fri/Sat some lines)
• Day cap: £8.50 zones 1-2, £15.20 zones 1-6
• Single journey zones 1-2: £3.40 peak, £2.90 off-peak
• Buses: £1.75 flat fare (no cash, card only)

⏰ **Timing Strategy**
• Avoid rush: 8-9:30am, 5-7pm (extremely crowded)
• Night Tube: Central, Jubilee, Northern, Piccadilly, Victoria lines
• Last tube varies: 11:30pm-1am depending on line
• Buses run 24/7 on major routes

💰 **Money-Saving Tips**
• Use Oyster/contactless - 50% cheaper than paper tickets
• Daily/weekly caps automatically applied
• Walk between nearby stations
• Railcards: 1/3 off if under 30 or over 60`,
    
    etiquette: `👥 **Queue Culture (SACRED)**
• British queuing is serious - never cut
• Form orderly lines even if just 2 people
• "After you" culture strong
• Tutting/disapproving looks for queue jumpers

🗣️ **Communication Style**
• "Please", "thank you", "sorry" used constantly
• Sorry even when not your fault
• Understatement common - "not bad" = excellent
• Small talk about weather perfectly normal

🚇 **Public Transport Rules**
• Stand right on escalators (walk left)
• Let passengers off before boarding
• Don't make eye contact (unspoken rule)
• Keep voice down on tube

🍺 **Pub Etiquette**
• Order at bar, no table service usually
• Tipping: 10-15% restaurants, pubs no tipping at bar
• Pub rounds: buy round for your group
• Last orders typically 11pm`,
    
    safety: `🚨 **Emergency Services**
• All emergencies: 999 or 112
• Non-emergency police: 101
• NHS non-emergency: 111
• Police wear high-vis jackets (approachable)

🛡️ **Safety Overview**
• Very safe city, low violent crime
• Tourist areas generally safest
• Alert in crowded areas (pickpockets rare but exist)
• Well-lit streets, good public transport safety

📍 **Areas to Be Aware**
• Some outer areas late at night
• Bike theft common - always lock
• Drink spiking in nightlife areas - watch drinks
• Moped phone snatching (hold phone away from road edge)`,
    
    food: `🍳 **British Food Culture**
• Full English breakfast: beans, eggs, sausage, bacon, toast, mushrooms, tomato £8-15
• Afternoon tea (3-5pm): sandwiches, scones, cakes £25-60
• Sunday roast tradition: roast meat, Yorkshire pudding, gravy, veg £12-20
• Pub culture central to British life

🍖 **Must-Try Dishes**
• Traditional full English breakfast
• Fish and chips from proper chippies
• Sunday roast with Yorkshire pudding
• Shepherd's pie or cottage pie
• Afternoon tea with scones and clotted cream
• Cornish pasty
• Sticky toffee pudding
• Proper British curry (chicken tikka masala)

🍺 **Dining Essentials**
• Gastropubs: elevated pub food
• Tipping: 10-15% if service not included (check bill)
• Curry capital: Indian food excellent
• Order at bar in pubs, no table service

☕ **Tea & Meal Times**
• Tea culture strong - offered constantly
• Milk in tea standard
• Breakfast: 7-10am
• Lunch: 12-2pm
• Afternoon tea: 3-5pm
• Dinner: 6-9pm (earlier than Continental Europe)`
  },

  'new york': {
    transport: `📱 **Apps & Tickets**
• MTA app - real-time subway info
• OMNY contactless or MetroCard: $2.90 per ride
• Citi Bike: $4.49 for 30min, $19/day pass
• Uber, Lyft for ride-sharing

🚇 **Subway System**
• 24/7 service on most lines
• 7-day unlimited: $34, single ride: $2.90
• Free transfers between subway/bus within 2 hours
• Express trains skip many stops - check carefully

⏰ **Transport Timing**
• Rush hours 7-9am, 5-7pm (very crowded)
• Late night service less frequent (10-20min waits)
• Subway runs 24/7 but some lines have night changes
• Weekend service changes common - check MTA alerts

💰 **Cost Breakdown**
• 7-day unlimited MetroCard: $34 (breaks even at 13 rides)
• Single ride: $2.90
• JFK to Manhattan: AirTrain $8.50 + subway $2.90 = $11.40
• Taxi to JFK: $52 flat rate + tolls + tip`,
    
    etiquette: `🚶 **New York Pace**
• Walk fast, keep right on sidewalks
• Don't block subway doors or stand in middle of sidewalk
• New Yorkers seem rushed but helpful when asked directly
• Direct communication appreciated - get to the point

🍽️ **Dining Culture**
• Tipping: 18-20% standard (double the tax)
• Fast casual dining common
• To-go culture strong
• Brunch reservations essential weekends

💳 **Tipping Standards**
• Restaurants: 18-20%
• Bars: $1-2 per drink
• Taxis: 15-20%
• Delivery: 15-20% minimum
• Hotel housekeeping: $2-5 per night

🗣️ **Communication**
• Direct, fast-paced conversation normal
• Eye contact and assertiveness expected
• Don't take brusqueness personally
• "How you doing?" is greeting, not actual question`,
    
    safety: `🚨 **Emergency Numbers**
• All emergencies: 911
• Non-emergency police: 311
• Tourist information: 311

🛡️ **Safety Guidelines**
• Generally safe but stay aware
• Subway safer than reputation - avoid empty cars late night
• Keep belongings secure in crowded areas
• Well-lit main streets safer than dark side streets

📍 **Neighborhood Awareness**
• Manhattan tourist areas very safe 24/7
• Avoid parks after dark except busiest areas
• Times Square crowded but safe
• Outer boroughs: stick to main areas at night`,
    
    food: `🥯 **NYC Food Culture**
• Bagels: $2-4 (with schmear and lox $8-12)
• Pizza by the slice: $3-5
• Food trucks: $5-10 meals
• Deli sandwiches: $8-15
• Fine dining: $50-200+ per person

🌆 **Meal Times**
• Breakfast: 7-11am (brunch Sat/Sun 10am-3pm)
• Lunch: 11:30am-2:30pm
• Dinner: 6-10pm (later on weekends)
• Late night: 24/7 diners and many pizza places

🍕 **Must-Try NYC Dishes**
• New York-style pizza (thin crust, fold the slice)
• Classic NYC bagel with lox and cream cheese
• Pastrami on rye from Jewish deli
• Hot dog from street cart
• Halal cart chicken over rice
• NYC cheesecake
• Black and white cookie
• Authentic NYC Chinese dumplings

💳 **Tipping & Payment**
• Restaurants: 18-20% (double the tax)
• Bars: $1-2 per drink
• Delivery: 15-20% minimum
• Water free at all restaurants
• Refills often free at casual places
• Tipping mandatory - check bill for auto-gratuity
• No smoking in restaurants or bars`
  },

  'barcelona': {
    transport: `📱 **Apps & Cards**
• TMB app for metro/bus
• T-10 card: €11.35 (10 rides, shareable)
• Hola Barcelona: €17.40/2 days, €25.50/3 days
• Uber, Cabify, Free Now for rides

🚇 **Metro & Transport**
• Metro runs 5am-midnight (Fri until 2am, Sat 24hrs, Sun until midnight)
• Single ticket: €2.40
• Airport T10 not valid - need separate ticket €5.50
• Last metro around midnight except weekends

⏰ **Transport Timing**
• Rush hours: 8-9:30am, 6-8pm
• Last metro: midnight weekdays, 2am Friday, 24hrs Saturday
• Bus night service (Nitbus) after metro closes
• Beach areas: extra crowded summer weekends

💰 **Pass Options**
• T-10 (10 rides): €11.35 - best value for short stays
• Hola BCN 2-day: €17.40
• Hola BCN 3-day: €25.50
• Single ride: €2.40 (expensive if multiple trips)`,
    
    etiquette: `🗣️ **Language & Culture**
• Learn both "Hola" (Spanish) and "Bon dia" (Catalan)
• Catalans proud of their distinct culture
• Many speak English but effort with Spanish/Catalan appreciated
• Avoid comparing Barcelona to rest of Spain

🍽️ **Dining Customs**
• Very late meal times: lunch 2-4pm, dinner 9-11pm
• Siesta closures 2-5pm common in small shops
• Tapas culture: share small plates
• Tipping: 5-10% for good service

⏰ **Social Schedule**
• Breakfast: 8-10am (café amb llet, pa amb tomàquet)
• Lunch: 2-4pm (main meal)
• Dinner: 9-11pm (later on weekends)
• Nightlife: bars 10pm-2am, clubs midnight-6am

🤝 **Social Behavior**
• Two cheek kisses greeting (even first meeting)
• Keep voices moderate in public transport
• Dress stylishly - locals notice
• "Mañana" attitude: relaxed pace of life`,
    
    safety: `🚨 **Emergency Numbers**
• Police: 091 (National) or 088 (Mossos - Catalan police)
• Medical: 061
• EU Emergency: 112
• Tourist Police: 93 256 24 30

🛡️ **Safety Awareness**
• Generally safe but pickpocketing very common
• Ramblas and Gothic Quarter: watch bags/phones constantly
• Metro lines 1 and 3: highest pickpocket rates
• Beach areas: never leave belongings unattended

⚠️ **Common Scams**
• Friendship bracelet tie (Ramblas)
• Fake petition signers (distract while partner steals)
• Card tricks or games
• Overpriced drinks in certain bars near port

📍 **Areas to Watch**
• La Rambla: extreme pickpocket zone
• Gothic Quarter: narrow streets, stay alert
• Raval: some areas sketchy at night
• Barceloneta beach: bag snatchers`,
    
    food: `🥘 **Catalan Food Culture**
• Pa amb tomàquet (bread with tomato): €2-4
• Tapas/Pintxos: €3-6 per plate
• Menu del dia (lunch special): €12-18 (3 courses + drink)
• Dinner mains: €15-25

🍷 **Meal Structure**
• Breakfast: café amb llet (coffee with milk), pastry €3-5
• Vermouth hour: Pre-lunch aperitif tradition
• Lunch: Biggest meal 2-4pm, often multi-course
• Dinner: Light tapas or full meal 9-11pm

🥂 **Drinking Culture**
• Cava (Catalan sparkling wine) popular and affordable €4-8
• Vermouth tradition: Sunday pre-lunch
• Wine with meals standard
• Café con leche: never order after 11am (locals judge)

🍖 **Must-Try Dishes**
• Pa amb tomàquet (tomato bread) - Catalan staple
• Paella (seafood or mixed, originally from Valencia)
• Fideuà (seafood noodle dish, like paella)
• Calcots with romesco sauce (seasonal)
• Patatas bravas (crispy potatoes with spicy sauce)
• Jamón ibérico (premium cured ham)
• Crema catalana (dessert, like crème brûlée)
• Escalivada (roasted vegetables)

💰 **Payment & Value**
• Service not included, tip 5-10%
• Menu del dia best value for lunch
• Tapas €3-8 each
• Splitting bills sometimes difficult`
  }
};

/**
 * Generate dynamic travel tips for any destination while maintaining exact format
 */
export const generateTravelTips = (destination: string, country: string): DestinationTips => {
  const destLower = destination.toLowerCase();
  
  // Check if we have specific data for this destination
  if (DESTINATION_SPECIFIC_TIPS[destLower]) {
    return DESTINATION_SPECIFIC_TIPS[destLower];
  }
  const normalizedCountry = country.toLowerCase();
  const normalizedDestination = destination.toLowerCase();
  
  // Get regional configuration
  const config = REGIONAL_CONFIGS[normalizedCountry] || getDefaultConfig(country);
  
  // Generate contextual tips based on destination characteristics
  const destinationType = categorizeDestination(normalizedDestination, normalizedCountry);
  
  return {
    transport: generateTransportTips(destination, config, destinationType),
    etiquette: generateEtiquetteTips(destination, config, destinationType),
    safety: generateSafetyTips(destination, config, destinationType),
    food: generateFoodTips(destination, config, destinationType)
  };
};

const getDefaultConfig = (country: string): RegionalConfig => ({
  currency: 'Local currency',
  language: 'Local language',
  timezone: 'Local timezone',
  emergencyNumbers: { police: '911', medical: '911', fire: '911' },
  plugType: 'Check locally',
  voltage: '110-240V (varies)',
  culturalNorms: ['Respect local customs', 'Dress appropriately', 'Learn basic phrases'],
  commonTransport: ['Local transport', 'Taxis', 'Walking'],
  foodSpecialties: ['Local cuisine', 'Fresh ingredients', 'Regional dishes'],
  safetyConsiderations: ['Use common sense', 'Stay aware', 'Keep valuables safe']
});

const categorizeDestination = (destination: string, country: string): string => {
  // Island destinations
  if (['bali', 'santorini', 'mykonos', 'capri', 'sicily', 'sardinia', 'corsica', 'crete', 'rhodes', 'hawaii', 'maldives', 'seychelles', 'mauritius', 'fiji', 'caribbean', 'barbados', 'jamaica', 'bahamas'].some(island => destination.includes(island))) {
    return 'island';
  }
  
  // Mountain/Alpine destinations
  if (['alps', 'chamonix', 'zermatt', 'innsbruck', 'aspen', 'whistler', 'banff', 'interlaken', 'grindelwald', 'st. moritz'].some(mountain => destination.includes(mountain))) {
    return 'mountain';
  }
  
  // Desert destinations
  if (['dubai', 'morocco', 'egypt', 'phoenix', 'las vegas', 'arizona', 'sahara', 'gobi'].some(desert => destination.includes(desert))) {
    return 'desert';
  }
  
  // Coastal destinations
  if (['coast', 'beach', 'riviera', 'amalfi', 'costa', 'marbella', 'nice', 'cannes', 'miami', 'gold coast'].some(coastal => destination.includes(coastal))) {
    return 'coastal';
  }
  
  // Major metropolitan areas
  if (['tokyo', 'london', 'paris', 'new york', 'los angeles', 'beijing', 'shanghai', 'mumbai', 'delhi', 'bangkok', 'singapore', 'hong kong', 'sydney', 'melbourne'].some(metro => destination.includes(metro))) {
    return 'metropolitan';
  }
  
  // Historic/cultural destinations
  if (['rome', 'athens', 'florence', 'venice', 'prague', 'vienna', 'budapest', 'jerusalem', 'cairo', 'kyoto', 'cusco', 'petra'].some(historic => destination.includes(historic))) {
    return 'historic';
  }
  
  return 'general';
};

const generateTransportTips = (destination: string, config: RegionalConfig, type: string): string => {
  const destinationApps = getDestinationSpecificApps(destination.toLowerCase());
  
  const specificApps = `📱 **Essential Apps & Services**\n${destinationApps}\n\n`;
  
  const transportMethods = `🚊 **Transport Options**\n${config.commonTransport.map(t => `• ${t}`).join('\n')}\n\n`;
  
  const costInfo = `💰 **Cost Optimization**\n• Day passes often better value than single rides\n• Peak vs off-peak pricing\n• Tourist passes with attraction bundles\n• Local payment methods (${config.currency})\n\n`;
  
  const timingTips = getTimingTips(type);
  
  return specificApps + transportMethods + costInfo + timingTips;
};

const generateEtiquetteTips = (destination: string, config: RegionalConfig, type: string): string => {
  const culturalNorms = `🤝 **Cultural Norms**\n${config.culturalNorms.map(norm => `• ${norm}`).join('\n')}\n\n`;
  
  const languageTips = `🗣️ **Communication**\n• Learn basic phrases in ${config.language}\n• Download translation apps\n• Locals appreciate language efforts\n• Non-verbal communication awareness\n\n`;
  
  const socialCustoms = getSocialCustoms(type, config);
  
  const paymentCulture = `💰 **Payment & Tipping**\n• Currency: ${config.currency}\n• Tipping customs vary by region\n• Cash vs card acceptance\n• Haggling appropriate in markets\n\n`;
  
  const plugType = config.plugType || 'Check locally (bring universal adapter)';
  const voltage = config.voltage || '220-240V, 50Hz (typical)';
  const practicalInfo = `🔌 **Electrical & Power**\n• Plug Type: ${plugType}\n• Voltage: ${voltage}\n• Bring universal adapter if your devices differ\n• Check device compatibility before plugging in\n\n`;
  
  return culturalNorms + languageTips + socialCustoms + paymentCulture + practicalInfo;
};

const generateSafetyTips = (destination: string, config: RegionalConfig, type: string): string => {
  const emergencyInfo = `🚨 **Emergency Contacts**\n• Police: ${config.emergencyNumbers.police}\n• Medical: ${config.emergencyNumbers.medical}\n• Fire: ${config.emergencyNumbers.fire}\n• Tourist helpline available in major areas\n\n`;
  
  const generalSafety = `🛡️ **General Safety**\n${config.safetyConsiderations.map(safety => `• ${safety}`).join('\n')}\n\n`;
  
  const specificRisks = getSpecificRisks(type);
  
  const healthInfo = `🏥 **Health Precautions**\n• Travel insurance recommended\n• Pharmacy locations and hours\n• Water safety guidelines\n• Sun protection (UV levels)\n\n`;
  
  return emergencyInfo + generalSafety + specificRisks + healthInfo;
};

const generateFoodTips = (destination: string, config: RegionalConfig, type: string): string => {
  const foodCulture = `🍽️ **Local Food Culture**\n${config.foodSpecialties.map(food => `• ${food}`).join('\n')}\n\n`;
  
  const mustTryDishes = getMustTryDishes(destination, config, type);
  
  const mealTiming = getMealTiming(config, type);
  
  const diningEtiquette = `🥢 **Dining Etiquette**\n• Table manners and customs\n• Sharing vs individual dishes\n• Alcohol consumption norms\n• Payment splitting customs\n\n`;
  
  const foodSafety = `📊 **Food Safety & Options**\n• Street food safety levels\n• Vegetarian/dietary restriction options\n• Water safety (tap vs bottled)\n• Allergy communication phrases\n\n`;
  
  return foodCulture + mustTryDishes + mealTiming + diningEtiquette + foodSafety;
};

const getTimingTips = (type: string): string => {
  switch (type) {
    case 'metropolitan':
      return '⏰ **Timing Strategy**\n• Avoid rush hours 7-9am, 5-7pm\n• Night transport limited\n• Weekend schedules different\n• Peak tourist season crowds\n\n';
    case 'island':
      return '⏰ **Island Transport**\n• Ferry schedules weather dependent\n• Book inter-island transport advance\n• Rental vehicles popular\n• Walking/cycling common short distances\n\n';
    case 'mountain':
      return '⏰ **Mountain Access**\n• Cable cars/lifts seasonal operation\n• Weather-dependent schedules\n• Early morning best for views\n• Equipment rental available\n\n';
    default:
      return '⏰ **Strategic Timing**\n• Plan around local schedules\n• Tourist season variations\n• Weather impact on transport\n• Early bookings recommended\n\n';
  }
};

const getSocialCustoms = (type: string, config: RegionalConfig): string => {
  return `🏛️ **Social Customs**\n• Greeting styles and personal space\n• Dress codes for different venues\n• Photography etiquette\n• Religious site respect\n\n`;
};

const getSpecificRisks = (type: string): string => {
  switch (type) {
    case 'island':
      return '🏖️ **Island-Specific Safety**\n• Ocean currents and tides\n• Sun exposure protection\n• Marine life awareness\n• Hurricane/typhoon seasons\n\n';
    case 'mountain':
      return '⛰️ **Mountain Safety**\n• Weather changes rapidly\n• Altitude sickness awareness\n• Proper equipment essential\n• Guide services recommended\n\n';
    case 'desert':
      return '🏜️ **Desert Precautions**\n• Extreme temperature variations\n• Hydration crucial\n• Sandstorm awareness\n• Navigation importance\n\n';
    case 'metropolitan':
      return '🏙️ **Urban Safety**\n• Pickpockets in tourist areas\n• Transport safety late night\n• Scam awareness\n• Emergency services readily available\n\n';
    default:
      return '⚠️ **Local Risks**\n• Research recent developments\n• Weather-related hazards\n• Cultural sensitivity areas\n• Tourist-specific precautions\n\n';
  }
};

const getMealTiming = (config: RegionalConfig, type: string): string => {
  return `⏰ **Meal Timing**\n• Breakfast: Local timing varies\n• Lunch: Main meal timing cultural\n• Dinner: Evening customs important\n• Snacking: Street food availability\n\n`;
};

const getMustTryDishes = (destination: string, config: RegionalConfig, type: string): string => {
  // Generate must-try dishes based on food specialties and destination type
  const dishes: string[] = [];
  
  // Add dishes based on specialties
  if (config.foodSpecialties.length > 0) {
    config.foodSpecialties.slice(0, 5).forEach(specialty => {
      dishes.push(specialty);
    });
  }
  
  // Add type-specific recommendations
  if (type === 'coastal' || type === 'island') {
    dishes.push('Fresh local seafood', 'Grilled fish specialties', 'Seafood market delicacies');
  } else if (type === 'mountain') {
    dishes.push('Hearty mountain stews', 'Local dairy products', 'Warming comfort foods');
  } else if (type === 'desert') {
    dishes.push('Traditional spiced dishes', 'Dates and local fruits', 'Mint tea variations');
  } else if (type === 'metropolitan') {
    dishes.push('Street food favorites', 'Local market specialties', 'Signature city dishes');
  }
  
  // Remove duplicates and limit to 8 dishes
  const uniqueDishes = Array.from(new Set(dishes)).slice(0, 8);
  
  return `🍴 **Must-Try Dishes**\n${uniqueDishes.map(dish => `• ${dish}`).join('\n')}\n\n`;
};

const getDestinationSpecificApps = (destination: string): string => {
  // Comprehensive destination-specific transport apps and services
  const destinationApps: Record<string, string> = {
    // European Major Cities
    'paris': '• **Official Apps:** Citymapper, Moovit, RATP (official metro)\n• **Ride Apps:** G7 Taxi, Uber, Bolt\n• **Bike Sharing:** Vélib (citywide network)\n• **Tickets:** Buy Navigo weekly pass (€30)',
    'london': '• **Official Apps:** TfL Go (official), Citymapper\n• **Ride Apps:** Uber, Bolt, Free Now\n• **Bike Sharing:** Santander Cycles (Boris Bikes)\n• **Payment:** Oyster Card or contactless payment',
    'rome': '• **Official Apps:** Citymapper Rome, Moovit, Roma Mobilità\n• **Ride Apps:** Free Now, Uber, ItTaxi\n• **Tickets:** Roma Pass includes transport\n• **Important:** Always validate tickets (€100+ fine)',
    'madrid': '• **Official Apps:** EMT Madrid, Metro de Madrid\n• **Ride Apps:** Uber, Cabify, Free Now\n• **Bike Sharing:** BiciMAD (electric bikes)\n• **Tickets:** Tourist Travel Pass available',
    'barcelona': '• **Official Apps:** TMB App (official metro/bus)\n• **Ride Apps:** Uber, Cabify, Free Now\n• **Bike Sharing:** Bicing (residents only)\n• **Tickets:** T-10 card for multiple trips',
    'berlin': '• **Official Apps:** BVG (official), DB Navigator\n• **Ride Apps:** Uber, Free Now, MyTaxi\n• **Bike Sharing:** Nextbike, Call a Bike\n• **Tickets:** WelcomeCard for unlimited transport',
    'amsterdam': '• **Official Apps:** GVB app, 9292 (trip planner)\n• **Ride Apps:** Uber, Bolt, Staxi\n• **Bike Rental:** Black Bikes, MacBike\n• **Payment:** GVB day passes, OV-chipkaart',
    'vienna': '• **Official Apps:** WienMobil, Wiener Linien\n• **Ride Apps:** Uber, Bolt, 40100 Taxi\n• **Bike Sharing:** CityBike Wien\n• **Tickets:** Vienna Card (transport + attractions)',
    'prague': '• **Official Apps:** PID Lítačka, DPP Info\n• **Ride Apps:** Uber, Bolt, Liftago\n• **Tickets:** Prague Castle + transport combos\n• **Payment:** Contactless cards accepted',
    'budapest': '• **Official Apps:** BudapestGO, BKK Futár\n• **Ride Apps:** Uber, Bolt, Főtaxi\n• **Bike Sharing:** MOL Bubi\n• **Tickets:** Budapest Card for discounts',
    'lisbon': '• **Official Apps:** Moovit, Citymapper\n• **Ride Apps:** Uber, Bolt, Mytaxi\n• **Bike Sharing:** GIRA (bike sharing)\n• **Tickets:** Lisboa Card (transport + attractions)',
    'athens': '• **Official Apps:** OASA Telematics, Moovit\n• **Ride Apps:** Uber, Beat (very popular), official taxis\n• **Tickets:** 3-day tourist tickets unlimited\n• **Metro:** Efficient to major archaeological sites',
    'florence': '• **Official Apps:** ATAF (bus system), Moovit\n• **Ride Apps:** Uber, Free Now, ItTaxi\n• **Walking:** Historic center best explored on foot\n• **Tickets:** Firenzecard includes public transport',
    'tokyo': '• **Official Apps:** Hyperdia, Japan Transit Planner, Tokyo Metro\n• **Ride Apps:** JapanTaxi, GO (formerly MOV), Uber (limited)\n• **IC Cards:** Suica, Pasmo for all transport\n• **Trains:** JR Yamanote Line circles city',
    'seoul': '• **Official Apps:** Citymapper Seoul, Subway Korea\n• **Ride Apps:** Kakao T (most popular), Uber\n• **Card:** T-money card for subway/bus\n• **Subway:** Extensive network, English signs',
    'beijing': '• **Official Apps:** Beijing Subway, Amap (Chinese)\n• **Ride Apps:** Didi Chuxing (Chinese app)\n• **Metro:** Extensive network, get physical card\n• **Language:** Apps mostly in Chinese',
    'shanghai': '• **Official Apps:** Shanghai Metro, Amap\n• **Ride Apps:** Didi Chuxing\n• **Metro:** World longest network\n• **Maglev:** Airport to Pudong (430 km/h)',
    'hong kong': '• **Official Apps:** MTR Mobile, Citymapper\n• **Ride Apps:** Uber, HKTaxi\n• **Octopus Card:** Essential for all transport\n• **Trams:** Historic double-decker trams',
    'singapore': '• **Official Apps:** MyTransport.SG, SG BusLeh\n• **Ride Apps:** Grab (dominant), Uber, Gojek\n• **MRT:** Extremely efficient system\n• **EZ-Link:** Card for MRT/buses',
    'bangkok': '• **Official Apps:** ViaBus, BTS/MRT official apps\n• **Ride Apps:** Grab (dominant), Bolt, InDriver\n• **BTS/MRT:** Modern skytrains & subway\n• **Tuk-tuks:** Negotiate prices first',
    'mumbai': '• **Official Apps:** m-Indicator, Mumbai Metro\n• **Ride Apps:** Uber, Ola (very popular)\n• **Local Trains:** Lifeline of Mumbai, crowded\n• **Auto-rickshaws:** Three-wheelers, use meter',
    'delhi': '• **Official Apps:** Delhi Metro Rail, Citymapper\n• **Ride Apps:** Uber, Ola, Rapido\n• **Metro:** Excellent network, tourist cards\n• **Auto-rickshaws:** Negotiate or use apps',
    'sydney': '• **Official Apps:** Tripview, Opal Travel\n• **Ride Apps:** Uber, DiDi, 13cabs\n• **Opal Card:** For trains, buses, ferries\n• **Ferries:** Scenic harbour transport',
    'melbourne': '• **Official Apps:** PTV Mobile, Citymapper\n• **Ride Apps:** Uber, DiDi, 13cabs\n• **Myki Card:** For trams, trains, buses\n• **Trams:** World largest tram network',
    'new york': '• **Official Apps:** MTA (official), Citymapper\n• **Ride Apps:** Uber, Lyft, Via\n• **MetroCard/OMNY:** For subway/buses\n• **Citi Bike:** Bike sharing system',
    'los angeles': '• **Official Apps:** Metro LA, Transit\n• **Ride Apps:** Uber, Lyft dominant\n• **TAP Card:** For metro/buses\n• **Car:** Still dominant transport mode',
    'toronto': '• **Official Apps:** TTC (official), Transit\n• **Ride Apps:** Uber, Lyft, Beck Taxi\n• **Presto Card:** For TTC, GO Transit\n• **Bike Share:** Toronto bike sharing',
    'dubai': '• **Official Apps:** Dubai Metro, RTA Dubai\n• **Ride Apps:** Uber, Careem (popular), Hala\n• **Nol Card:** For metro/buses/trams\n• **Dubai Metro:** Driverless, air-conditioned',
    'istanbul': '• **Official Apps:** İBB CepTrafik, Moovit\n• **Ride Apps:** Uber, BiTaksi, iTaksi\n• **Istanbulkart:** For all transport\n• **Ferries:** Bosphorus crossings scenic'
  };
  
  // Return specific apps for destination or general guidance
  return destinationApps[destination] || 
    '• **Research Apps:** Download local transport apps\n• **International:** Google Maps, Uber, Bolt\n• **Payment:** Check local payment methods\n• **Offline Maps:** Download before arrival';
};